import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import posthog from 'posthog-js';
import NavBar from '../components/navBar';
import useApi from '../hooks/useApi';
import usePageSeo from '../utils/usePageSeo';
import shopProducts from '../data/shopProducts';

function viewToSearch(view) {
  if (!view || view === 'home' || view === 'dashboard') return '';
  return `?view=${encodeURIComponent(view)}`;
}

function isTokenValid(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 > Date.now();
  } catch {
    return false;
  }
}

export default function ShopPage() {
  usePageSeo({
    title: 'Shop',
    description:
      'Browse books and reading essentials on Poppyruz. As an Amazon Associate, we earn from qualifying purchases.',
    path: '/shop',
  });

  const navigate = useNavigate();
  const { request } = useApi();
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    setIsAuthenticated(Boolean(token && isTokenValid(token)));
  }, []);

  const handleNavigate = useCallback(
    (view) => {
      navigate({ pathname: '/', search: viewToSearch(view) });
    },
    [navigate],
  );

  const handleLogout = useCallback(async () => {
    try {
      await request({
        method: 'post',
        url: '/api/v1/users/logout/',
        withAuth: true,
        data: { refresh: localStorage.getItem('refresh_token') },
      });
    } catch (err) {
      console.error('Logout API error:', err);
    }
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_id');
    localStorage.removeItem('name');
    setIsAuthenticated(false);
    handleNavigate('home');
  }, [request, handleNavigate]);

  const handleProductClick = useCallback((product) => {
    posthog.capture('amazon_affiliate_click', {
      product_id: product.id,
      product_title: product.title,
    });
  }, []);

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <NavBar
        isAuthenticated={isAuthenticated}
        onLogout={handleLogout}
        currentView="shop"
        onNavigate={handleNavigate}
      />
      <main className="mx-auto max-w-6xl px-6 pb-16 pt-24">
        <header className="mb-10 text-center">
          <h1 className="text-3xl font-bold text-gray-900 md:text-4xl">Shop</h1>
          <p className="mx-auto mt-3 max-w-2xl text-xs leading-relaxed text-gray-500">
            As an Amazon Associate, Poppyruz earns from qualifying purchases. Product images and availability
            are provided by Amazon; clicking a product opens Amazon.in in a new tab.
          </p>
        </header>

        <ul className="grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
          {shopProducts.map((product) => (
            <li key={product.id} className="flex flex-col">
              <a
                href={product.affiliateUrl}
                target="_blank"
                rel="noopener noreferrer sponsored"
                onClick={() => handleProductClick(product)}
                className="group flex flex-1 flex-col items-center text-center focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-800 focus-visible:ring-offset-2"
              >
                <div className="flex aspect-[2/3] w-full items-center justify-center overflow-hidden bg-gray-50">
                  {product.imageUrl ? (
                    <img
                      src={product.imageUrl}
                      alt={product.title}
                      loading="lazy"
                      className="h-full w-full object-contain transition-opacity group-hover:opacity-90"
                    />
                  ) : (
                    <span className="px-2 text-center text-xs text-gray-400">No image</span>
                  )}
                </div>
                <h2 className="mt-3 line-clamp-2 text-sm font-medium text-gray-900 group-hover:text-violet-900">
                  {product.title}
                </h2>
                <span className="mt-2 text-sm font-semibold text-violet-900">View on Amazon</span>
              </a>
            </li>
          ))}
        </ul>

        <p className="mt-12 border-t border-gray-100 pt-6 text-center text-xs text-gray-500">
          As an Amazon Associate, we earn from qualifying purchases.
        </p>
      </main>
    </div>
  );
}
